const FOCUSABLE_SELECTORS = [
  "input",
  "button",
  "select",
  "textarea",
  "a",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]:not(.ql-clipboard)',

  ".ui-button",
  ".portal-searchbar__input",
];

const WRAPPER_SELECTORS = [".ui-selectonemenu", ".ui-button", ".portal-searchbar"];

let isKeyboardMode = false;

export function initAccessibility() {
  registerInputModeListeners();
  registerFocusHandlers();
  registerQuillEditorEscapeEvent();
}

/**
 * Focuses the first focusable element within a given container.
 * @param {HTMLElement} container
 * @returns
 */
export function focusFirstFocusable(container) {
  if (!container) {
    return;
  }

  const focusableSelector = FOCUSABLE_SELECTORS.join(",");

  const focusable = container.querySelector(focusableSelector);
  if (focusable instanceof HTMLElement) {
    focusable.focus();
  }
}

function registerQuillEditorEscapeEvent() {
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") {
      return;
    }

    const active = document.activeElement;
    const quillEditor = active?.closest(".ql-editor");

    if (!quillEditor) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const focusables = Array.from(document.querySelectorAll(FOCUSABLE_SELECTORS.join(","))).filter(
      (el) =>
        el instanceof HTMLElement &&
        (!("disabled" in el) || !el.disabled) &&
        el.offsetParent !== null &&
        (el.tabIndex !== -1 || el.classList.contains("ql-editor")),
    );
    if (!(active instanceof HTMLElement)) {
      return;
    }
    const index = focusables.indexOf(active);

    if (index >= 0 && index < focusables.length - 1) {
      const nextElement = focusables[index + 1];
      if (nextElement && nextElement instanceof HTMLElement) {
        nextElement.focus();
      }
    } else {
      if (quillEditor instanceof HTMLElement) {
        quillEditor.blur();
      }
    }
  });
}

function registerInputModeListeners() {
  document.addEventListener("keydown", (event) => {
    if (
      event.key === "Tab" ||
      event.key.startsWith("Arrow") ||
      event.key === "Home" ||
      event.key === "End" ||
      event.key === "Escape"
    ) {
      isKeyboardMode = true;
    }
  });

  document.addEventListener("mousedown", () => {
    isKeyboardMode = false;
  });
  document.addEventListener("pointerdown", () => {
    isKeyboardMode = false;
  });
}

function registerFocusHandlers() {
  document.addEventListener("focusin", handleFocusIn);
  document.addEventListener("focusout", handleFocusOut);
}

/**
 * @param {HTMLElement} el
 */
function isFocusableElement(el) {
  return FOCUSABLE_SELECTORS.some((sel) => el.matches(sel));
}

/**
 * @param {HTMLElement} el
 */
function getWrapperElements(el) {
  return WRAPPER_SELECTORS.map((sel) => el.closest(sel)).filter((wrapper) => wrapper !== null);
}

/**
 * @param {Event} event
 */
function handleFocusIn(event) {
  const el = event.target;
  if (!(el instanceof HTMLElement)) {
    return;
  }
  if (!isFocusableElement(el)) {
    return;
  }
  if (isKeyboardMode) {
    el.classList.add("kb-focus");
    for (const wrapper of getWrapperElements(el)) {
      wrapper.classList.add("kb-focus");
    }
  } else {
    el.classList.remove("kb-focus");
    for (const wrapper of getWrapperElements(el)) {
      wrapper.classList.remove("kb-focus");
    }
  }
}

/**
 * @param {Event} event
 */
function handleFocusOut(event) {
  const el = event.target;
  if (!(el instanceof HTMLElement)) {
    return;
  }
  el.classList.remove("kb-focus");
  for (const wrapper of getWrapperElements(el)) {
    wrapper.classList.remove("kb-focus");
  }
}
